import { useEffect, useState } from "react";
import { PencilIcon, TrashIcon, PlusIcon } from "@heroicons/react/outline";
import {
  createCompany,
  getCompanies,
  deleteCompany,
  updateCompany,
} from "../../api/companiesApi";
import PageLoader from "../../components/pageLoader";
import Toast from "../../components/toast";
import ToastContainer from "../../components/toastContainer";
import CompanyFormModal from "./CompanyFormModal";
import DeleteConfirmationModal from "../../components/deleteModal";
import { useNavigate } from "react-router-dom";

const CompaniesInfoPage = () => {
  const [showAddModal, setShowAddModal] = useState(false);

  const LIMIT = 10;

  const [companies, setCompanies] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [deleteCompanyId, setDeleteCompanyId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const totalPages = Math.ceil(total / LIMIT);

  const [editingCompany, setEditingCompany] = useState(null);
  const [saving, setSaving] = useState(false);

  const [toast, setToast] = useState(null);

  const navigate = useNavigate();

  const getPageNumbers = () => {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  };

  const handleCompanySubmit = async (info) => {
    try {
      setSaving(true);

      const payload = {
        name: info.name,
        reg_no: info.reg_no,
        vat_no: info.vat_no,
        accountant_name: info.accountant_name,
        accountant_phone: info.accountant_phone,
        company_id: info.company_id,
        euid: info.euid,
        address: {
          address_1: info.addressLine1,
          address_2: info.addressLine2,
          address_3: info.addressLine3,
          city: info.city,
          country: info.country,
          county: info.county,
          postcode: info.postcode,
        },
      };

      if (editingCompany) {
        await updateCompany(editingCompany.id, payload);
      } else {
        await createCompany(payload);
      }

      setEditingCompany(null);
      setShowAddModal(false);

      // Refresh list
      const offset = (page - 1) * LIMIT;
      const data = await getCompanies(LIMIT, offset);
      setCompanies(data.results || []);
      setTotal(data.count || 0);
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail || err?.detail || "Something went wrong",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCompany = async () => {
    try {
      setDeleting(true);
      await deleteCompany(deleteCompanyId);

      setDeleteCompanyId(null);

      // Refresh companies list
      const offset = (page - 1) * LIMIT;
      const data = await getCompanies(LIMIT, offset);
      setCompanies(data.results || []);
      setTotal(data.count || 0);
    } catch (err) {
      alert("Failed to delete company");
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setLoading(true);
        setError("");

        const offset = (page - 1) * LIMIT;
        const data = await getCompanies(LIMIT, offset);

        setCompanies(data.results || []);
        setTotal(data.count || 0);
      } catch (err) {
        setError("Failed to load companies.");
      } finally {
        setLoading(false);
      }
    };

    fetchCompanies();
  }, [page]);


  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white">Companies Info</h1>
          <p className="text-sm text-slate-400">
            Manage companies and their details.
          </p>
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
        >
          <PlusIcon className="w-4 h-4" />
          Create Company
        </button>
      </div>

      {error && <div className="p-6 text-red-400">{error}</div>}

      {/* Table */}
      <div className="bg-slate-900/60 border border-white/10 rounded-xl">
        <div className="overflow-x-auto">
          <table className="min-w-[900px] w-full border-collapse">
            <thead>
              <tr className="border-b border-white/10 text-left text-xs uppercase tracking-wider text-slate-400">
                <th className="px-6 py-4">ID</th>
                <th className="px-6 py-4">VAT Code</th>
                <th className="px-6 py-4">Company Name</th>
                <th className="px-6 py-4">Country</th>
                <th className="px-6 py-4">Income</th>
                <th className="px-6 py-4">Outstanding Income</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              {companies.map((company) => (
                <tr
                  key={company.id}
                  className="border-b border-white/5 last:border-none hover:bg-slate-800/40 transition"
                >
                  <td
                    onClick={() => navigate(`/dashboard/companies/${company.id}`)}
                    className="px-6 py-4 underline underline-offset-4 decoration-pink-600 text-sm text-white"
                  >
                    {company.id}
                  </td>

                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-slate-300">
                      {company.vat_no}
                    </div>
                  </td>

                  <td className="px-6 py-4 text-sm text-slate-300">
                    {company.name}
                  </td>

                  <td className="px-6 py-4 text-sm text-slate-300">
                    {company.address?.country}
                  </td>

                  <td className="px-6 py-4 text-sm text-slate-300">
                    {company.income}
                  </td>

                  <td className="px-6 py-4 text-sm text-slate-300">
                    {company.outstanding_income}
                  </td>

                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setEditingCompany(company)}
                        className="p-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-700 transition"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>

                      <button
                        onClick={() => setDeleteCompanyId(company.id)}
                        className="p-2 rounded-md text-slate-300 hover:text-red-400 hover:bg-red-500/10 transition"
                        title="Delete company"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty state placeholder (future) */}
        {companies.length === 0 && (
          <div className="p-8 text-center text-slate-400">
            No companies found.
          </div>
        )}
      </div>

      <div className="flex justify-end">
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 text-sm text-slate-400">
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 px-6 py-4 text-sm text-slate-400">
                {/* Previous */}
                <button
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                  className={`px-3 py-1 rounded-md border border-white/10 transition
        ${
          page === 1
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-slate-800 text-white"
        }`}
                >
                  Previous
                </button>

                {/* Page Numbers */}
                <div className="flex gap-1">
                  {getPageNumbers().map((pageNumber) => (
                    <button
                      key={pageNumber}
                      onClick={() => setPage(pageNumber)}
                      className={`px-3 py-1 rounded-md border border-white/10 transition
            ${
              page === pageNumber
                ? "bg-pink-600 text-white"
                : "text-slate-300 hover:bg-slate-800 hover:text-white"
            }`}
                    >
                      {pageNumber}
                    </button>
                  ))}
                </div>

                {/* Next */}
                <button
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className={`px-3 py-1 rounded-md border border-white/10 transition
        ${
          page === totalPages
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-slate-800 text-white"
        }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <CompanyFormModal
        isOpen={showAddModal || !!editingCompany}
        onClose={() => {
          setShowAddModal(false);
          setEditingCompany(null);
        }}
        onSubmit={handleCompanySubmit}
        loading={saving}
        mode={editingCompany ? "edit" : "create"}
        initialData={editingCompany}
      />

      <DeleteConfirmationModal
        isOpen={!!deleteCompanyId}
        description="Are you sure you want to delete this company?  This action cannot be undone."
        onClose={() => setDeleteCompanyId(null)}
        onConfirm={handleDeleteCompany}
        loading={deleting}
      />

      <ToastContainer>
        {toast && (
          <div className="toast-enter">
            <Toast
              type={toast.type}
              message={toast.message}
              onClose={() => setToast(null)}
            />
          </div>
        )}
      </ToastContainer>
    </div>
  );
};

export default CompaniesInfoPage;
